import { useSelector } from "react-redux";
import { LinkSimple } from "@phosphor-icons/react";
import { selectCanUseGrow } from "../../store/slices/authSlice";
import { useGetGrowLinksForPatientQuery } from "../../store/services/growApi";
import { formatHebrewDate } from "../../utils/dateUtils";
import GrowLinkStatusChip from "./GrowLinkStatusChip";
import RequestPaymentButton from "./RequestPaymentButton";
import "./GrowLinks.css";

const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString("he-IL", { maximumFractionDigits: 2 })}`;

/**
 * GrowPatientLinksHistory — patient payments tab block:
 *  - header action "בקשת תשלום לכל החוב" → ONE combined link for every pending
 *    transaction that has no active link yet (`pendingTransactions`)
 *  - last 10 links of the patient with status chips (click → popover via onOpenLink)
 * Renders nothing when the user cannot use Grow or there is nothing to show.
 */
export default function GrowPatientLinksHistory({ clinicId, patientId, patient, pendingTransactions = [], onOpenLink }) {
  const canUseGrow = useSelector(selectCanUseGrow);
  const { data: links = [] } = useGetGrowLinksForPatientQuery(
    { clinicId, patientId, limitCount: 10 },
    { skip: !clinicId || !patientId || !canUseGrow }
  );

  if (!canUseGrow) return null;
  const debt = pendingTransactions.reduce((a, t) => a + (Number(t.amount) || 0), 0);
  if (pendingTransactions.length === 0 && links.length === 0) return null;

  return (
    <section className="grow-ph" aria-label="דרישות תשלום Grow">
      <header className="grow-ph-head">
        <span className="grow-ph-title">
          <LinkSimple size={15} weight="fill" />
          דרישות תשלום Grow
        </span>
        {pendingTransactions.length > 0 && (
          <RequestPaymentButton
            transactions={pendingTransactions}
            patient={patient}
            size="sm"
            variant="primary"
            label={
              pendingTransactions.length === 1
                ? `דרישת תשלום · ${fmtMoney(debt)}`
                : `דרישת תשלום לכל החוב · ${fmtMoney(debt)} · ${pendingTransactions.length} תשלומים`
            }
          />
        )}
      </header>

      {links.length > 0 && (
        <ul className="grow-ph-list">
          {links.map((link) => (
            <li key={link.id}>
              <button type="button" className="grow-ph-row" onClick={() => onOpenLink?.(link)}>
                <span className="grow-ph-sum">{fmtMoney(link.sum)}</span>
                <span className="grow-ph-meta">
                  {link.createdAt ? formatHebrewDate(link.createdAt) : "—"}
                  {(link.transactionIds || []).length > 1 ? ` · ${link.transactionIds.length} תשלומים` : ""}
                  {link.sentVia ? ` · ${link.sentVia === "whatsapp" ? "וואטסאפ" : link.sentVia === "sms" ? "SMS" : link.sentVia}` : ""}
                </span>
                <GrowLinkStatusChip link={link} size="xs" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
